import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Plus,
  Building,
  ShieldAlert,
  LogOut,
  Users2,
  ShieldCheck,
  Globe2,
  LogIn,
  MoreVertical,
  Search,
  Pencil,
  Ban,
  Play,
  XCircle,
  Trash2,
  X,
  Database,
  Activity,
  FileText,
  Server,
  Copy,
  Check,
  ArrowRight,
  ArrowLeft,
  MessageCircle,
  CreditCard,
  Download,
  ClipboardCheck,
  Crown,
  Clock,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { useAuthStore, type ImpersonatePayload } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { formatDateTime, formatRelative } from '@/lib/format';
import { UsageTrendCard } from '@/components/superadmin/UsageTrendCard';
import { downloadAsCsv, csvFilename } from '@/lib/download';
import {
  NbhActivityRow,
  NbhFeatureTile,
  NbhGreeting,
  NbhSectionHeader,
  NbhStatTile,
  nbhPalette
} from '@/components/ui/nbh';

type TenantStatus = 'trial' | 'active' | 'suspended' | 'cancelled';

// Catalog-driven: tenants can now be on any tier code from PlanTier table.
// Kept as a `string` here so custom codes (e.g. "premium") flow through.
type SubscriptionPlan = string;
type SubscriptionPlanStatus = 'active' | 'past_due' | 'cancelled';

// Plan-tier catalog row — shape returned by GET /v1/superadmin/plan-tiers.
interface PlanTierCatalogRow {
  code: string;
  label: string;
  description: string | null;
  price_inr_per_month: number;
  seats_cap_units: number | null;
  sort_order: number;
  is_active: boolean;
  tenants_count?: number;
}
interface PlanTierListResponse {
  items: PlanTierCatalogRow[];
}

// Map known plan codes to badge variant. Unknown codes (custom tiers from the
// catalog) safely fall back to 'muted'.
function getPlanBadgeVariant(code: string): 'muted' | 'info' | 'ok' | 'warn' {
  const KNOWN: Record<string, 'muted' | 'info' | 'ok' | 'warn'> = {
    free: 'muted',
    lite: 'info',
    pro: 'ok',
    enterprise: 'warn'
  };
  return KNOWN[code] ?? 'muted';
}

interface SocietyRow {
  id: string;
  name: string;
  jurisdiction_tag: string;
  country: string;
  city: string | null;
  state: string | null;
  timezone: string;
  tenant_status: TenantStatus;
  suspended_at: string | null;
  suspended_reason: string | null;
  trial_expires_at: string | null;
  feature_face_recog_enabled: boolean;
  feature_anpr_enabled: boolean;
  plan: SubscriptionPlan;
  plan_status: SubscriptionPlanStatus;
  plan_price_inr_per_month: number | null;
  plan_seats_units: number | null;
  plan_renews_at: string | null;
  created_at: string;
  _count?: { units: number; residents: number; guards: number; admins?: number };
}

// Legacy alias kept for clarity at call-sites; resolves via the catalog-safe lookup.
const PLAN_BADGE = getPlanBadgeVariant;

interface SocietyAdminRow {
  id: string;
  full_name: string;
  phone: string;
  email: string | null;
  role: 'admin' | 'accountant' | 'treasurer' | 'secretary' | 'president';
  active: boolean;
  created_at: string;
}

const ROLE_BADGE_VARIANT: Record<SocietyAdminRow['role'], 'default' | 'muted' | 'info' | 'warn'> = {
  admin: 'default',
  accountant: 'muted',
  treasurer: 'muted',
  secretary: 'info',
  president: 'warn'
};

interface SocietyListResponse {
  items: SocietyRow[];
  next_cursor: string | null;
}

const JURISDICTIONS = ['IN-DPDP', 'EU-GDPR', 'US-IL-BIPA', 'AE-PDPL', 'OTHER'] as const;

const TENANT_STATUSES = ['trial', 'active', 'suspended', 'cancelled'] as const;
const SORT_OPTIONS: Array<{ value: 'newest' | 'oldest' | 'name_asc' | 'name_desc' | 'units_desc'; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'name_desc', label: 'Name Z–A' },
  { value: 'units_desc', label: 'Units (highest)' }
];

const STATUS_BADGE: Record<TenantStatus, { label: string; variant: 'warn' | 'ok' | 'alert' | 'muted' }> = {
  trial: { label: 'Trial', variant: 'warn' },
  active: { label: 'Active', variant: 'ok' },
  suspended: { label: 'Suspended', variant: 'alert' },
  cancelled: { label: 'Cancelled', variant: 'muted' }
};

// Step 1 — society details. Validated standalone on "Continue".
const CreateStep1Schema = z.object({
  name: z.string().min(2, 'Name required').max(160),
  jurisdiction_tag: z.enum(JURISDICTIONS),
  country: z.string().length(2, 'ISO-2 country code'),
  city: z.string().max(80).optional(),
  state: z.string().max(80).optional(),
  timezone: z.string().min(2)
});

// Step 2 — first society admin. Validated on final "Provision".
const CreateStep2Schema = z.object({
  first_admin_full_name: z.string().min(2, 'Required').max(120),
  first_admin_phone: z.string().regex(/^\+?[1-9]\d{6,14}$/, 'E.164 phone'),
  first_admin_email: z.union([z.string().email('Invalid email'), z.literal('')]).optional()
});

// Combined schema reused for the full form's resolver — react-hook-form
// validates touched fields per-step but the final submit reads the whole
// payload. Splitting validation into two trigger() calls (one per step)
// gives us the wizard UX without a second form instance.
const CreateSchema = CreateStep1Schema.merge(CreateStep2Schema);
type CreateForm = z.infer<typeof CreateSchema>;

type Step1Field = keyof z.infer<typeof CreateStep1Schema>;
const STEP1_FIELDS: Step1Field[] = ['name', 'jurisdiction_tag', 'country', 'city', 'state', 'timezone'];

/**
 * Server response from POST /v1/superadmin/societies, including the dev
 * temp-OTP surfacing block that ships with the new provisioning flow.
 */
interface ProvisionResponse extends SocietyRow {
  first_admin: {
    id: string;
    full_name: string;
    phone: string;
    email: string | null;
  } | null;
  first_admin_setup: {
    phone: string;
    dev_otp?: string;
    expires_at: string;
    login_url: string;
  } | null;
}

const EditSchema = z.object({
  name: z.string().min(2, 'Name required').max(160),
  jurisdiction_tag: z.enum(JURISDICTIONS),
  country: z.string().length(2, 'ISO-2 country code'),
  city: z.string().max(80).optional().or(z.literal('')),
  state: z.string().max(80).optional().or(z.literal('')),
  timezone: z.string().min(2).max(64),
  trial_expires_at: z.string().optional().or(z.literal(''))
});
type EditForm = z.infer<typeof EditSchema>;

type SortValue = (typeof SORT_OPTIONS)[number]['value'];
type StatusFilter = 'all' | TenantStatus;

export function SuperAdminDashboardPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  // Two-step wizard inside the provision dialog. Reset to 1 every time
  // the dialog opens so re-runs always start at the top.
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  // Post-create credentials dialog — shows the surfaced dev OTP +
  // copyable login URL / WhatsApp deep link. Driven by mutation success.
  const [postCreate, setPostCreate] = useState<ProvisionResponse | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const userId = useAuthStore((s) => s.user?.id);

  // ──────────────── Filter / search / sort state ────────────────
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState(''); // debounced
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [jurisdictionFilter, setJurisdictionFilter] = useState<'' | (typeof JURISDICTIONS)[number]>('');
  const [sort, setSort] = useState<SortValue>('newest');
  const [pages, setPages] = useState<SocietyListResponse[]>([]);

  // debounce search input → q (300ms)
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Whenever filters/sort/q change, clear cached pages so we restart from page 1
  useEffect(() => {
    setPages([]);
  }, [q, statusFilter, jurisdictionFilter, sort]);

  const filtersActive = q !== '' || statusFilter !== 'all' || jurisdictionFilter !== '';

  const buildQS = (cursor?: string | null): string => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (statusFilter !== 'all') p.set('tenant_status', statusFilter);
    if (jurisdictionFilter) p.set('jurisdiction_tag', jurisdictionFilter);
    p.set('sort', sort);
    p.set('limit', '20');
    if (cursor) p.set('cursor', cursor);
    return p.toString();
  };

  const lastCursor = pages.length > 0 ? pages[pages.length - 1]!.next_cursor : null;

  const list = useQuery<SocietyListResponse>({
    queryKey: ['superadmin', 'societies', { q, statusFilter, jurisdictionFilter, sort, cursor: lastCursor }],
    queryFn: () => get<SocietyListResponse>(`/superadmin/societies?${buildQS(lastCursor)}`),
    // Disable refetch when we're sitting on accumulated pages — only fire when query key changes
    placeholderData: (prev) => prev
  });

  // Append each new page when it lands
  useEffect(() => {
    if (!list.data) return;
    setPages((prev) => {
      // If this is the page-1 fetch (no cursor in lastCursor before this call), reset
      const incomingFirstId = list.data!.items[0]?.id;
      const alreadyIncluded = prev.some((p) => p.items[0]?.id === incomingFirstId && incomingFirstId !== undefined);
      if (alreadyIncluded) return prev;
      // Determine if this was a cursor fetch — if prev's last next_cursor matches what we asked for
      const fetchedAsContinuation = prev.length > 0 && prev[prev.length - 1]!.next_cursor !== null;
      return fetchedAsContinuation ? [...prev, list.data!] : [list.data!];
    });
  }, [list.data]);

  const items: SocietyRow[] = useMemo(() => pages.flatMap((p) => p.items), [pages]);
  const nextCursor = pages.length > 0 ? pages[pages.length - 1]!.next_cursor : null;

  const totalUnits = items.reduce((s, x) => s + (x._count?.units ?? 0), 0);
  const totalResidents = items.reduce((s, x) => s + (x._count?.residents ?? 0), 0);
  const totalGuards = items.reduce((s, x) => s + (x._count?.guards ?? 0), 0);

  // Revenue tracking — MRR sums plan_price across active+past_due tenants;
  // ARR is the simple 12x projection. Free/cancelled/null prices excluded.
  const billableItems = items.filter(
    (x) => x.tenant_status === 'active' && (x.plan_status === 'active' || x.plan_status === 'past_due')
  );
  const mrrInr = billableItems.reduce((s, x) => s + (x.plan_price_inr_per_month ?? 0), 0);
  const arrInr = mrrInr * 12;
  const paidTenants = billableItems.filter((x) => (x.plan_price_inr_per_month ?? 0) > 0).length;
  const freeTenants = billableItems.length - paidTenants;
  const pastDueTenants = items.filter((x) => x.plan_status === 'past_due').length;
  const trialingTenants = items.filter((x) => x.tenant_status === 'trial').length;
  const suspendedTenants = items.filter((x) => x.tenant_status === 'suspended').length;
  const activeTenants = items.filter((x) => x.tenant_status === 'active').length;
  const planBreakdown = items.reduce<Record<string, number>>((acc, x) => {
    const key = x.plan ?? 'free';
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  const clearFilters = (): void => {
    setSearchInput('');
    setQ('');
    setStatusFilter('all');
    setJurisdictionFilter('');
    setSort('newest');
  };

  const invalidateList = (): void => {
    setPages([]);
    void queryClient.invalidateQueries({ queryKey: ['superadmin', 'societies'] });
  };

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: {
      name: '',
      jurisdiction_tag: 'IN-DPDP',
      country: 'IN',
      city: '',
      state: '',
      timezone: 'Asia/Kolkata',
      first_admin_full_name: '',
      first_admin_phone: '',
      first_admin_email: ''
    }
  });

  const create = useMutation({
    mutationFn: (values: CreateForm) =>
      post<ProvisionResponse, CreateForm>('/superadmin/societies', {
        ...values,
        first_admin_email: values.first_admin_email?.trim() ? values.first_admin_email : undefined
      }),
    onSuccess: (s) => {
      toast.success(`"${s.name}" provisioned`);
      invalidateList();
      setOpen(false);
      form.reset();
      setWizardStep(1);
      // Pop the post-create dialog so the super-admin can copy the
      // first-admin temp OTP / login URL to share with the new RWA.
      setPostCreate(s);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  /**
   * Reset the wizard whenever the dialog reopens. Avoids stale "Step 2"
   * state if the operator cancelled out mid-flow.
   */
  useEffect(() => {
    if (open) setWizardStep(1);
  }, [open]);

  /**
   * Step 1 → Step 2 transition. Validates ONLY the step-1 fields with
   * react-hook-form's `trigger`; if any fail, the inline error messages
   * stay visible and we don't advance.
   */
  const handleStep1Continue = async (): Promise<void> => {
    const ok = await form.trigger(STEP1_FIELDS);
    if (ok) setWizardStep(2);
  };

  const handleSignOut = () => {
    clear();
    navigate({ to: '/superadmin/login' });
  };

  // ─── CSV export ──────────────────────────────────────────────
  // Mirrors the same filter state used by the list query so the CSV
  // contains exactly what the operator sees on screen.
  const [exporting, setExporting] = useState(false);
  const handleExportCsv = async (): Promise<void> => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (statusFilter !== 'all') p.set('tenant_status', statusFilter);
      if (jurisdictionFilter) p.set('jurisdiction_tag', jurisdictionFilter);
      p.set('sort', sort);
      const url = `/superadmin/societies/export.csv${p.toString() ? `?${p.toString()}` : ''}`;
      const result = await downloadAsCsv(url, csvFilename('tenants'));
      toast.success(`Downloaded ${result.rowCount} rows`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const addDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4" /> Provision tenant
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {wizardStep === 1 ? 'Step 1 of 2 · Society details' : 'Step 2 of 2 · First admin'}
          </DialogTitle>
          <DialogDescription>
            {wizardStep === 1
              ? 'Where is this society and which privacy regime governs it?'
              : 'Seed the first SocietyAdmin so the RWA can log in via OTP and onboard residents.'}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator — two segments, current one highlighted. */}
        <div className="flex items-center gap-vt-2 pb-vt-1" aria-hidden="true">
          <div
            className={
              'h-1 flex-1 rounded-vt-pill transition-colors ' +
              (wizardStep >= 1 ? 'bg-vt-primary' : 'bg-vt-border')
            }
          />
          <div
            className={
              'h-1 flex-1 rounded-vt-pill transition-colors ' +
              (wizardStep >= 2 ? 'bg-vt-primary' : 'bg-vt-border')
            }
          />
        </div>

        <form onSubmit={form.handleSubmit((v) => create.mutate(v))} className="flex flex-col gap-vt-4">
          {/* Step 1 fields. Rendered hidden in step 2 so RHF state survives.
              Using `hidden` (not unmount) keeps the field values + dirty/touched
              state intact when the user goes Back from step 2. */}
          <div className={wizardStep === 1 ? 'flex flex-col gap-vt-4' : 'hidden'}>
            <div className="grid grid-cols-2 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="name">Society name</Label>
                <Input id="name" autoFocus={wizardStep === 1} {...form.register('name')} />
                {form.formState.errors.name ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.name.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="jurisdiction_tag">Jurisdiction</Label>
                <Select id="jurisdiction_tag" {...form.register('jurisdiction_tag')}>
                  {JURISDICTIONS.map((j) => (
                    <option key={j} value={j}>
                      {j}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="country">Country (ISO-2)</Label>
                <Input id="country" maxLength={2} {...form.register('country')} />
                {form.formState.errors.country ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.country.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="city">City</Label>
                <Input id="city" {...form.register('city')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="state">State</Label>
                <Input id="state" {...form.register('state')} />
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="timezone">Timezone</Label>
              <Input id="timezone" placeholder="Asia/Kolkata" {...form.register('timezone')} />
              {form.formState.errors.timezone ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.timezone.message}</p>
              ) : null}
            </div>
          </div>

          {/* Step 2 — first society admin. */}
          <div className={wizardStep === 2 ? 'flex flex-col gap-vt-4' : 'hidden'}>
            <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
              <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                First society admin (logs in via OTP)
              </legend>
              <div className="grid grid-cols-2 gap-vt-3">
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="first_admin_full_name">Full name</Label>
                  <Input
                    id="first_admin_full_name"
                    autoFocus={wizardStep === 2}
                    {...form.register('first_admin_full_name')}
                  />
                  {form.formState.errors.first_admin_full_name ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">
                      {form.formState.errors.first_admin_full_name.message}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="first_admin_phone">Phone (E.164)</Label>
                  <Input
                    id="first_admin_phone"
                    placeholder="+919812345678"
                    {...form.register('first_admin_phone')}
                  />
                  {form.formState.errors.first_admin_phone ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">
                      {form.formState.errors.first_admin_phone.message}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="first_admin_email">Email (optional)</Label>
                <Input id="first_admin_email" type="email" {...form.register('first_admin_email')} />
                {form.formState.errors.first_admin_email ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">
                    {form.formState.errors.first_admin_email.message}
                  </p>
                ) : null}
              </div>
            </fieldset>
          </div>

          <DialogFooter>
            {wizardStep === 1 ? (
              <>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={() => void handleStep1Continue()}>
                  Continue <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={() => setWizardStep(1)}>
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <Button type="submit" loading={create.isPending} loadingText="Provisioning…">
                  Provision tenant
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-h-screen bg-vt-bg">
      {/* Top bar — distinct from society admin shell */}
      <header className="sticky top-0 z-30 border-b border-vt-border bg-vt-surface/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-vt-6 py-vt-3">
          <div className="flex items-center gap-vt-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)] shadow-vt-1">
              <ShieldAlert className="h-4 w-4" strokeWidth={2} />
            </span>
            <div>
              <div className="text-vt-13 font-semibold leading-none text-vt-text">HoodDesk SaaS Console</div>
              <div className="text-vt-12 leading-tight text-vt-text-soft">Super-admin · cross-tenant</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleSignOut} title={userId ? `User ${userId.slice(0, 8)}…` : ''}>
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <PageHeader
        title="All societies"
        description="Provision and manage every tenant in the HoodDesk SaaS estate."
        actions={
          <div className="flex items-center gap-vt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleExportCsv()}
              loading={exporting}
              loadingText="Exporting…"
            >
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            {addDialog}
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-6 px-vt-6 pb-vt-8 pt-vt-4">
        {/* NBH greeting strip — single-line status summary derived from the
            same /societies list as the KPI tiles below. */}
        <NbhGreeting
          greeting="Super-admin console"
          status={
            list.isLoading
              ? 'Loading platform snapshot…'
              : `${items.length} tenant${items.length === 1 ? '' : 's'} · ₹${mrrInr.toLocaleString('en-IN')} MRR · trial ${trialingTenants} · suspended ${suspendedTenants}`
          }
        />

        {/* KPI strip — NBH pastel tiles. Palette chosen to mirror the mobile
            resident home (lavender / sky / mint / peach). */}
        {/* KPI tiles — semantic palette:
              lavender = tenants (organisations / entities)
              sky      = unit count (raw inventory / data)
              mint     = residents (people we serve)
              salmon   = guards (security / alert-role) */}
        <div className="grid grid-cols-2 gap-vt-4 md:grid-cols-4">
          <NbhStatTile
            palette="lavender"
            icon={Globe2}
            label="Tenants"
            value={list.isLoading ? <Skeleton className="h-9 w-20" /> : items.length}
            sub={list.isLoading ? undefined : `${activeTenants} active`}
          />
          <NbhStatTile
            palette="sky"
            icon={Building}
            label="Total units"
            value={list.isLoading ? <Skeleton className="h-9 w-20" /> : totalUnits}
            sub={list.isLoading ? undefined : `across ${items.length} societies`}
          />
          <NbhStatTile
            palette="mint"
            icon={Users2}
            label="Residents"
            value={list.isLoading ? <Skeleton className="h-9 w-20" /> : totalResidents}
          />
          <NbhStatTile
            palette="salmon"
            icon={ShieldCheck}
            label="Guards"
            value={list.isLoading ? <Skeleton className="h-9 w-20" /> : totalGuards}
          />
        </div>

        {/* SaaS tools — semantic palette:
              sky      = Tenants list (data / index)
              peach    = Billing (money)
              mint     = Plans (catalog / approved offerings)
              yellow   = Leads (marketing / attention)
              lavender = Team (people)
              blush    = HR (people-ops / culture)
              salmon   = Audit log (scrutiny / oversight) */}
        <section className="space-y-vt-3">
          <NbhSectionHeader
            title="SaaS tools"
            subtitle="Jump into cross-tenant operations."
            palette="lavender"
          />
          <div className="grid grid-cols-3 gap-vt-3 sm:grid-cols-4 lg:grid-cols-7">
            <NbhFeatureTile label="Tenants" icon={Globe2} palette="sky" to="/superadmin/dashboard" />
            <NbhFeatureTile label="Billing" icon={CreditCard} palette="peach" to="/superadmin/billing" />
            <NbhFeatureTile label="Plans" icon={FileText} palette="mint" to="/superadmin/plans" />
            <NbhFeatureTile label="Leads" icon={MessageCircle} palette="yellow" to="/superadmin/marketing-leads" />
            <NbhFeatureTile label="Team" icon={Users2} palette="lavender" to="/superadmin/users" />
            <NbhFeatureTile label="HR" icon={Building} palette="blush" to="/superadmin/hr" />
            <NbhFeatureTile label="Audit log" icon={FileText} palette="salmon" to="/superadmin/audit" />
          </div>
        </section>

        {/* Revenue + plan-mix tracking — derived from the same /societies
            list, so it auto-updates as plans are changed via kebab. MRR sums
            plan_price across billable tenants; suspended/cancelled excluded. */}
        <div>
          <NbhSectionHeader
            title="Revenue & plan mix"
            subtitle="Auto-derived from active tenants"
            palette="mint"
          />
          <Card>
            <CardContent className="space-y-vt-3 p-vt-4">
              <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
                <div className="rounded-vt-md bg-vt-surface-2 p-vt-3">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-vt-text-soft">MRR</div>
                  <div className="mt-vt-1 font-mono text-vt-20 font-semibold text-vt-text">
                    ₹{mrrInr.toLocaleString('en-IN')}
                  </div>
                  <div className="text-vt-12 text-vt-text-muted">{paidTenants} paid · {freeTenants} free</div>
                </div>
                <div className="rounded-vt-md bg-vt-surface-2 p-vt-3">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-vt-text-soft">ARR (12×)</div>
                  <div className="mt-vt-1 font-mono text-vt-20 font-semibold text-vt-text">
                    ₹{arrInr.toLocaleString('en-IN')}
                  </div>
                  <div className="text-vt-12 text-vt-text-muted">projected annualised</div>
                </div>
                <div className="rounded-vt-md bg-vt-surface-2 p-vt-3">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-vt-text-soft">Past-due / suspended</div>
                  <div className="mt-vt-1 flex items-center gap-vt-2">
                    <span className="font-mono text-vt-20 font-semibold text-vt-alert">{pastDueTenants}</span>
                    <span className="text-vt-12 text-vt-text-soft">past-due ·</span>
                    <span className="font-mono text-vt-16 font-semibold text-vt-text">{suspendedTenants}</span>
                    <span className="text-vt-12 text-vt-text-soft">suspended</span>
                  </div>
                  <div className="text-vt-12 text-vt-text-muted">{trialingTenants} trial</div>
                </div>
                <div className="rounded-vt-md bg-vt-surface-2 p-vt-3">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-vt-text-soft">Plan mix</div>
                  <div className="mt-vt-1 flex flex-wrap gap-vt-1">
                    {Object.entries(planBreakdown)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([p, count]) =>
                        count ? (
                          <Badge key={p} variant={PLAN_BADGE(p)} className="capitalize">
                            {p} · {count}
                          </Badge>
                        ) : null
                      )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ──────── Platform-level insights row 1 ────────
            Recent platform events (audit feed) + trial / expiring tenants.
            Two-column on md+, single-column on small. */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2">
          <RecentPlatformEventsCard />
          <TrialExpiringTenantsCard items={items} />
        </div>

        {/* ──────── Platform-level insights row 2 ────────
            Top tenants by units + revenue trend sparkline. */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2">
          <TopTenantsByUnitsCard items={items} />
          <RevenueTrendCard />
        </div>

        {/* System health — polls every 30s. Read-only ops snapshot. */}
        <div>
          <NbhSectionHeader
            title="System health"
            subtitle="Live ops snapshot, polled every 30s"
            palette="sky"
          />
          <SystemHealthCard />
        </div>

        {/* Cross-tenant usage trend — 30/90-day platform-wide series. Sits
            between system health and the filter bar per V0.2 dashboard
            information-density brief. */}
        <div>
          <NbhSectionHeader
            title="Platform trends"
            subtitle="Last 30 days, cross-tenant"
            palette="lavender"
          />
          <UsageTrendCard
            title="Platform trends · last 30 days"
            endpoint="/superadmin/trends"
            includeActiveTenants
            queryKey={['superadmin', 'trends']}
          />
        </div>

        {/* Filter bar */}
        <div>
          <NbhSectionHeader
            title="All tenants"
            subtitle="Search, filter, and provision new societies"
            palette="peach"
          />
          <Card>
          <CardContent className="flex flex-col gap-vt-3 p-vt-4 md:flex-row md:flex-wrap md:items-end">
            <div className="flex min-w-[220px] flex-1 flex-col gap-vt-1">
              <Label htmlFor="tenant-search">Search</Label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-vt-text-soft"
                  aria-hidden="true"
                />
                <Input
                  id="tenant-search"
                  type="search"
                  placeholder="Search by name, city, state…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label>Status</Label>
              <TabsPills
                value={statusFilter}
                onChange={(v) => setStatusFilter(v as typeof statusFilter)}
                options={(['all', ...TENANT_STATUSES] as const).map((s) => ({
                  value: s,
                  label: s === 'all' ? 'All' : STATUS_BADGE[s].label
                }))}
                aria-label="Tenant status filter"
              />
            </div>
            <div className="flex flex-col gap-vt-1 md:w-48">
              <Label htmlFor="tenant-jurisdiction">Jurisdiction</Label>
              <Select
                id="tenant-jurisdiction"
                value={jurisdictionFilter}
                onChange={(e) => setJurisdictionFilter(e.target.value as typeof jurisdictionFilter)}
              >
                <option value="">Any</option>
                {JURISDICTIONS.map((j) => (
                  <option key={j} value={j}>
                    {j}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1 md:w-52">
              <Label htmlFor="tenant-sort">Sort</Label>
              <Select
                id="tenant-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortValue)}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            {filtersActive ? (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="self-end">
                <X className="h-4 w-4" /> Clear filters
              </Button>
            ) : null}
          </CardContent>
        </Card>
        </div>

        {/* Tenants table */}
        <Card>
          <CardContent className="p-vt-4">
            {list.isLoading && pages.length === 0 ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError && pages.length === 0 ? (
              <EmptyState
                icon={<Globe2 className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load tenants"
                description={getErrorMessage(list.error)}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : items.length === 0 && filtersActive ? (
              <EmptyState
                icon={<Search className="h-6 w-6" strokeWidth={1.75} />}
                title="No tenants match the filters"
                description="Try widening the search or clearing one of the filters above."
                action={
                  <Button variant="secondary" onClick={clearFilters}>
                    <X className="h-4 w-4" /> Clear filters
                  </Button>
                }
              />
            ) : items.length === 0 ? (
              <EmptyState
                icon={<Globe2 className="h-6 w-6" strokeWidth={1.75} />}
                title="No tenants yet"
                description="Provision your first society to start running pilots."
                action={
                  <Button onClick={() => setOpen(true)}>
                    <Plus className="h-4 w-4" /> Provision first tenant
                  </Button>
                }
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Society</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Jurisdiction</TableHead>
                      <TableHead className="text-right">Admins</TableHead>
                      <TableHead className="text-right">Units</TableHead>
                      <TableHead className="text-right">Residents</TableHead>
                      <TableHead className="text-right">Guards</TableHead>
                      <TableHead>Provisioned</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((s) => (
                      <SocietyTableRow key={s.id} society={s} onMutated={invalidateList} />
                    ))}
                  </TableBody>
                </Table>
                {nextCursor ? (
                  <div className="flex justify-center pt-vt-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void list.refetch()}
                      loading={list.isFetching}
                      loadingText="Loading…"
                    >
                      Load more
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Post-provision creds dialog — surfaces the dev OTP / login URL so
          the super-admin can immediately hand the new RWA chairman their
          first-login bundle without leaving the dashboard. */}
      <PostProvisionDialog result={postCreate} onClose={() => setPostCreate(null)} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// System health card — reads /v1/superadmin/system/health every 30s and
// renders mini-stats for DB, tenants, audit-log volume, document storage
// + the API process itself. Read-only ops surface; no mutations.
// ──────────────────────────────────────────────────────────────────────

interface SystemHealthSnapshot {
  db: { size_bytes: number; table_count: number };
  tenants: {
    total: number;
    active: number;
    trial: number;
    suspended: number;
    cancelled: number;
  };
  audit_log: {
    total_rows: number;
    last_24h_rows: number;
    newest_event_at: string | null;
  };
  uploads: {
    documents_total: number;
    storage_bytes_total: number;
  };
  api: {
    node_version: string;
    uptime_seconds: number;
    memory_rss_bytes: number;
  };
  checked_at: string;
}

/**
 * "1.4 GB" / "823 MB" / "12 KB" / "0 B" — locale-free, 1-decimal compact.
 * Uses 1024-based units (KiB/MiB-style scaling) but labels with the
 * everyday KB/MB/GB suffix the founder expects on an ops card.
 */
function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  // No decimal for raw bytes, 1 decimal for KB+
  return `${i === 0 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
}

/**
 * "3h 22m" for uptimes < 24h, "5d 4h" beyond — readable on the card
 * without leaking precision the operator doesn't need.
 */
function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0m';
  const total = Math.floor(seconds);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function SystemHealthCard(): JSX.Element {
  const health = useQuery<SystemHealthSnapshot>({
    queryKey: ['superadmin', 'system-health'],
    queryFn: () => get<SystemHealthSnapshot>('/superadmin/system/health'),
    // Poll every 30s for live ops monitoring. Keep prior data visible
    // through transient errors so the card doesn't flicker on a hiccup.
    refetchInterval: 30_000,
    placeholderData: (prev) => prev
  });

  const d = health.data;
  const stats: Array<{
    icon: JSX.Element;
    tone: string;
    label: string;
    value: string;
    sub?: string;
  }> = d
    ? [
        {
          icon: <Database className="h-5 w-5" strokeWidth={1.75} />,
          tone: 'bg-vt-primary-100 text-vt-primary',
          label: 'Database',
          value: formatBytes(d.db.size_bytes),
          sub: `${d.db.table_count} tables`
        },
        {
          icon: <Globe2 className="h-5 w-5" strokeWidth={1.75} />,
          tone: 'bg-[color:var(--vt-info-100)] text-[color:var(--vt-info)]',
          label: 'Tenants by status',
          value: `${d.tenants.active} active`,
          sub: `${d.tenants.trial} trial · ${d.tenants.suspended} susp · ${d.tenants.cancelled} cancl`
        },
        {
          icon: <Activity className="h-5 w-5" strokeWidth={1.75} />,
          tone: 'bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]',
          label: 'Audit (24h)',
          value: `${d.audit_log.last_24h_rows.toLocaleString('en-IN')}`,
          sub: `${d.audit_log.total_rows.toLocaleString('en-IN')} total`
        },
        {
          icon: <FileText className="h-5 w-5" strokeWidth={1.75} />,
          tone: 'bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]',
          label: 'Documents',
          value: `${d.uploads.documents_total.toLocaleString('en-IN')}`,
          sub: formatBytes(d.uploads.storage_bytes_total)
        },
        {
          icon: <Server className="h-5 w-5" strokeWidth={1.75} />,
          tone: 'bg-vt-primary-100 text-vt-primary',
          label: 'API uptime',
          value: formatUptime(d.api.uptime_seconds),
          sub: `${formatBytes(d.api.memory_rss_bytes)} RSS`
        }
      ]
    : [];

  return (
    <Card>
      <CardContent className="p-vt-4">
        <div className="mb-vt-3 flex items-center justify-between">
          <div>
            <div className="text-vt-14 font-semibold text-vt-text">System health</div>
            <div className="text-vt-12 text-vt-text-soft">
              {health.isError
                ? 'Could not load snapshot'
                : d
                  ? `Updated ${formatDateTime(d.checked_at)} · refreshes every 30s`
                  : 'Loading…'}
            </div>
          </div>
        </div>
        {health.isLoading && !d ? (
          <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : health.isError && !d ? (
          <div className="text-vt-13 text-vt-text-muted">{getErrorMessage(health.error)}</div>
        ) : (
          <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-5">
            {stats.map((s) => (
              <div
                key={s.label}
                className="flex items-center gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3"
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md ${s.tone}`}
                >
                  {s.icon}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-vt-12 text-vt-text-muted">{s.label}</div>
                  <div className="truncate text-vt-16 font-semibold text-vt-text">{s.value}</div>
                  {s.sub ? (
                    <div className="truncate text-vt-12 text-vt-text-soft">{s.sub}</div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Post-provision dialog — surfaces the first-admin temp OTP + login URL
// + a WhatsApp deep-link greeting so the super-admin can immediately
// hand off credentials to the new RWA chairman. Closes via "Done".
// ──────────────────────────────────────────────────────────────────────

/**
 * Tiny copyable-pill component: clicking copies the value to clipboard
 * and flips the icon to a checkmark for 1.5s as a tactile confirm.
 */
function CopyableField({
  label,
  value,
  mono = false,
  large = false
}: {
  label: string;
  value: string;
  mono?: boolean;
  large?: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  const onCopy = (): void => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="flex flex-col gap-vt-1">
      <Label>{label}</Label>
      <div className="flex items-center gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-2">
        <div
          className={
            'flex-1 truncate ' +
            (mono ? 'font-mono ' : '') +
            (large ? 'text-vt-20 font-semibold tracking-wider text-vt-text' : 'text-vt-13 text-vt-text')
          }
          title={value}
        >
          {value}
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onCopy} aria-label={`Copy ${label}`}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

function PostProvisionDialog({
  result,
  onClose
}: {
  result: ProvisionResponse | null;
  onClose: () => void;
}): JSX.Element {
  const open = result !== null;
  const setup = result?.first_admin_setup ?? null;
  const admin = result?.first_admin ?? null;
  const loginUrl = setup?.login_url ?? 'http://localhost:5273/login';
  // Append /login if the server returned a bare origin so the share text
  // links directly to the OTP page rather than the marketing homepage.
  const finalLoginUrl = loginUrl.endsWith('/login') ? loginUrl : `${loginUrl.replace(/\/$/, '')}/login`;
  const otp = setup?.dev_otp;
  const phone = setup?.phone ?? admin?.phone ?? '';
  const name = admin?.full_name ?? 'Admin';

  // Prebuilt WhatsApp deep link. wa.me opens the platform-default WhatsApp
  // client with the text prefilled — no recipient is set so the operator
  // picks the chat manually (we don't always know which number the RWA
  // contact uses for WhatsApp vs their login phone).
  const greeting =
    otp && phone
      ? `Hello ${name}, your HoodDesk account is ready. Login at ${finalLoginUrl} with phone ${phone}, OTP ${otp}. Expires in 10 minutes.`
      : `Hello ${name}, your HoodDesk account is ready. Login at ${finalLoginUrl} with phone ${phone}. Use "Request OTP" to receive your code.`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(greeting)}`;

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-[color:var(--vt-safe)]">Tenant provisioned</DialogTitle>
          <DialogDescription>
            Share these credentials with the new RWA chairman so they can log in and start onboarding.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col gap-vt-3">
            <div className="grid grid-cols-2 gap-vt-3">
              <CopyableField label="Society name" value={result.name} />
              <CopyableField label="Society ID" value={result.id} mono />
            </div>

            {admin ? (
              <div className="grid grid-cols-2 gap-vt-3">
                <CopyableField label="Admin name" value={admin.full_name} />
                <CopyableField label="Admin phone" value={admin.phone} mono />
              </div>
            ) : null}

            {otp ? (
              <div className="flex flex-col gap-vt-2 rounded-vt-md border border-[color:var(--vt-warn)] bg-[color:var(--vt-warn-100)]/40 p-vt-3">
                <CopyableField label="Temporary OTP" value={otp} mono large />
                <p className="text-vt-12 text-vt-text-muted">
                  Share this once; expires in 10 minutes. The OTP is consumed on first successful verify.
                </p>
              </div>
            ) : (
              <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-13 text-vt-text-muted">
                Temp OTP not available — the RWA can request one from the login page.
              </div>
            )}

            <CopyableField label="Login URL" value={finalLoginUrl} />

            <div className="flex flex-col gap-vt-1">
              <Label>WhatsApp greeting</Label>
              <textarea
                readOnly
                value={greeting}
                rows={3}
                className="w-full resize-none rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-2 text-vt-13 text-vt-text"
              />
              <div className="flex justify-end">
                <Button
                  asChild
                  variant="secondary"
                  size="sm"
                  aria-label="Open WhatsApp with greeting prefilled"
                >
                  <a href={waHref} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-4 w-4" /> Open WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SocietyTableRow({ society: s, onMutated }: { society: SocietyRow; onMutated: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const navigate = useNavigate();
  const adminsQuery = useQuery({
    queryKey: ['superadmin', 'society-admins', s.id],
    queryFn: () => get<{ items: SocietyAdminRow[] }>(`/superadmin/societies/${s.id}/admins`),
    enabled: open
  });

  // "Open as admin" — mints a tenant-scoped JWT, snapshots the super-admin
  // session into the store, then drops us on the society admin dashboard
  // with the impersonation banner pinned above.
  const impersonate = useMutation({
    mutationFn: () => post<ImpersonatePayload>(`/superadmin/societies/${s.id}/impersonate`),
    onSuccess: (res) => {
      const ok = useAuthStore.getState().startImpersonation(res);
      if (!ok) {
        toast.error('Could not start impersonation — re-login as super-admin and retry.');
        return;
      }
      toast.success(`Opened "${res.society.name}" as society admin`);
      void navigate({ to: '/dashboard' });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ──────────── Edit form ────────────
  const editForm = useForm<EditForm>({
    resolver: zodResolver(EditSchema),
    defaultValues: {
      name: s.name,
      jurisdiction_tag: (s.jurisdiction_tag.replace('_', '-') as (typeof JURISDICTIONS)[number]) ?? 'IN-DPDP',
      country: s.country,
      city: s.city ?? '',
      state: s.state ?? '',
      timezone: s.timezone,
      trial_expires_at: s.trial_expires_at ? s.trial_expires_at.slice(0, 16) : ''
    }
  });

  // Re-seed form when row data updates (e.g. after a save round-trip)
  useEffect(() => {
    if (!editOpen) return;
    editForm.reset({
      name: s.name,
      jurisdiction_tag: (s.jurisdiction_tag.replace('_', '-') as (typeof JURISDICTIONS)[number]) ?? 'IN-DPDP',
      country: s.country,
      city: s.city ?? '',
      state: s.state ?? '',
      timezone: s.timezone,
      trial_expires_at: s.trial_expires_at ? s.trial_expires_at.slice(0, 16) : ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOpen, s.id]);

  const editMutation = useMutation({
    mutationFn: (values: EditForm) =>
      patch<SocietyRow>(`/superadmin/societies/${s.id}`, {
        name: values.name,
        jurisdiction_tag: values.jurisdiction_tag,
        country: values.country,
        city: values.city?.trim() ? values.city : null,
        state: values.state?.trim() ? values.state : null,
        timezone: values.timezone,
        trial_expires_at: values.trial_expires_at?.trim()
          ? new Date(values.trial_expires_at).toISOString()
          : null
      }),
    onSuccess: () => {
      toast.success(`"${s.name}" updated`);
      setEditOpen(false);
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const suspendMutation = useMutation({
    mutationFn: (reason: string) =>
      post<SocietyRow>(`/superadmin/societies/${s.id}/suspend`, { reason }),
    onSuccess: () => {
      toast.success(`"${s.name}" suspended`);
      setSuspendOpen(false);
      setSuspendReason('');
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const unsuspendMutation = useMutation({
    mutationFn: () => post<SocietyRow>(`/superadmin/societies/${s.id}/unsuspend`, {}),
    onSuccess: () => {
      toast.success(`"${s.name}" reactivated`);
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const cancelMutation = useMutation({
    mutationFn: () => post<SocietyRow>(`/superadmin/societies/${s.id}/cancel`, {}),
    onSuccess: () => {
      toast.success(`"${s.name}" cancelled`);
      setCancelOpen(false);
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteMutation = useMutation({
    mutationFn: () => del<void>(`/superadmin/societies/${s.id}`),
    onSuccess: () => {
      toast.success(`"${s.name}" deleted`);
      setDeleteOpen(false);
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const statusInfo = STATUS_BADGE[s.tenant_status] ?? STATUS_BADGE.active;
  const isSuspended = s.tenant_status === 'suspended';
  const isCancelled = s.tenant_status === 'cancelled';
  const isActiveOrTrial = s.tenant_status === 'active' || s.tenant_status === 'trial';

  return (
    <>
      <TableRow>
        <TableCell>
          <Link
            to="/superadmin/tenants/$id"
            params={{ id: s.id }}
            className="flex items-center gap-vt-3 rounded-vt-sm outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
            title="Open tenant detail"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary">
              {s.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="truncate font-medium text-vt-text hover:text-vt-primary">{s.name}</div>
              <div className="truncate font-mono text-vt-12 text-vt-text-soft">{s.id.slice(0, 8)}…</div>
            </div>
          </Link>
        </TableCell>
        <TableCell>
          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
          {isSuspended && s.suspended_reason ? (
            <div
              className="mt-0.5 max-w-[180px] truncate text-vt-12 text-vt-text-soft"
              title={s.suspended_reason}
            >
              {s.suspended_reason}
            </div>
          ) : null}
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-0.5">
            <Badge variant={PLAN_BADGE(s.plan)} className="w-fit capitalize">
              {s.plan}
            </Badge>
            <span className="text-vt-12 text-vt-text-soft">
              {s.plan_price_inr_per_month === null
                ? 'Contact us'
                : s.plan_price_inr_per_month === 0
                  ? 'Free'
                  : `₹${s.plan_price_inr_per_month.toLocaleString('en-IN')}/mo`}
              {s.plan_status === 'past_due' ? ' · ⚠ past due' : ''}
              {s.plan_status === 'cancelled' ? ' · cancelled' : ''}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-vt-text-muted">
          {s.city ?? '—'}{s.state ? `, ${s.state}` : ''} <span className="text-vt-text-soft">·</span> {s.country}
        </TableCell>
        <TableCell>
          <Badge variant="muted" className="font-mono">
            {s.jurisdiction_tag.replace('_', '-')}
          </Badge>
        </TableCell>
        <TableCell className="text-right font-mono text-vt-13">{s._count?.admins ?? 0}</TableCell>
        <TableCell className="text-right font-mono text-vt-13">{s._count?.units ?? 0}</TableCell>
        <TableCell className="text-right font-mono text-vt-13">{s._count?.residents ?? 0}</TableCell>
        <TableCell className="text-right font-mono text-vt-13">{s._count?.guards ?? 0}</TableCell>
        <TableCell className="text-vt-text-muted">{formatDateTime(s.created_at)}</TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-vt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => impersonate.mutate()}
              loading={impersonate.isPending}
              loadingText="Opening…"
              disabled={impersonate.isPending}
              title="Open as society admin (impersonate)"
              aria-label="Open as society admin (impersonate)"
            >
              <LogIn className="h-4 w-4" /> Open as admin
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
              {open ? 'Hide admins' : 'View admins'}
            </Button>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  aria-label="Tenant actions"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-vt-sm text-vt-text-muted transition-colors duration-vt hover:bg-vt-surface-2 hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={6}
                  className="z-50 min-w-52 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface p-1 shadow-vt-3 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                >
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                    onSelect={() => setPlanOpen(true)}
                  >
                    <CreditCard className="h-3.5 w-3.5" /> Change plan
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                    onSelect={() => setEditOpen(true)}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit details
                  </DropdownMenu.Item>
                  {!isSuspended && !isCancelled ? (
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                      onSelect={() => setSuspendOpen(true)}
                    >
                      <Ban className="h-3.5 w-3.5" /> Suspend
                    </DropdownMenu.Item>
                  ) : null}
                  {isSuspended ? (
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                      onSelect={() => unsuspendMutation.mutate()}
                    >
                      <Play className="h-3.5 w-3.5" /> Unsuspend
                    </DropdownMenu.Item>
                  ) : null}
                  {isActiveOrTrial ? (
                    <>
                      <DropdownMenu.Separator className="my-1 h-px bg-vt-border" />
                      <DropdownMenu.Item
                        className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-warn outline-none data-[highlighted]:bg-vt-warn/10"
                        onSelect={() => setCancelOpen(true)}
                      >
                        <XCircle className="h-3.5 w-3.5" /> Cancel tenant
                      </DropdownMenu.Item>
                    </>
                  ) : null}
                  {isCancelled ? (
                    <>
                      <DropdownMenu.Separator className="my-1 h-px bg-vt-border" />
                      <DropdownMenu.Item
                        className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-alert outline-none data-[highlighted]:bg-vt-alert/10"
                        onSelect={() => setDeleteOpen(true)}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete permanently
                      </DropdownMenu.Item>
                    </>
                  ) : null}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow>
          <TableCell colSpan={11} className="bg-vt-surface-2/40">
            {adminsQuery.isLoading ? (
              <Skeleton className="h-12 w-full" />
            ) : adminsQuery.data ? (
              <div className="space-y-vt-2 py-vt-2">
                <div className="text-vt-12 font-semibold uppercase tracking-wider text-vt-text-soft">
                  Society admins · {adminsQuery.data.items.length}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Role</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Added</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adminsQuery.data.items.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Badge variant={ROLE_BADGE_VARIANT[a.role] ?? 'muted'} className="capitalize">
                            {a.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{a.full_name}</TableCell>
                        <TableCell className="font-mono text-vt-13">{a.phone}</TableCell>
                        <TableCell className="text-vt-text-muted">{a.email ?? '—'}</TableCell>
                        <TableCell className="text-vt-text-muted">{formatDateTime(a.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-vt-2 text-vt-text-soft">Failed to load admins</div>
            )}
          </TableCell>
        </TableRow>
      ) : null}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit tenant — {s.name}</DialogTitle>
            <DialogDescription>
              Updates the society profile. All fields are optional; only changed values are persisted.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={editForm.handleSubmit((v) => editMutation.mutate(v))}
            className="flex flex-col gap-vt-4"
          >
            <div className="grid grid-cols-2 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`edit-name-${s.id}`}>Society name</Label>
                <Input id={`edit-name-${s.id}`} {...editForm.register('name')} />
                {editForm.formState.errors.name ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">
                    {editForm.formState.errors.name.message}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`edit-juris-${s.id}`}>Jurisdiction</Label>
                <Select id={`edit-juris-${s.id}`} {...editForm.register('jurisdiction_tag')}>
                  {JURISDICTIONS.map((j) => (
                    <option key={j} value={j}>
                      {j}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`edit-country-${s.id}`}>Country (ISO-2)</Label>
                <Input id={`edit-country-${s.id}`} maxLength={2} {...editForm.register('country')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`edit-city-${s.id}`}>City</Label>
                <Input id={`edit-city-${s.id}`} {...editForm.register('city')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`edit-state-${s.id}`}>State</Label>
                <Input id={`edit-state-${s.id}`} {...editForm.register('state')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`edit-tz-${s.id}`}>Timezone</Label>
                <Input id={`edit-tz-${s.id}`} placeholder="Asia/Kolkata" {...editForm.register('timezone')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor={`edit-trial-${s.id}`}>Trial expires (optional)</Label>
                <Input
                  id={`edit-trial-${s.id}`}
                  type="datetime-local"
                  {...editForm.register('trial_expires_at')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={editMutation.isPending} loadingText="Saving…">
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change-plan dialog */}
      <ChangePlanDialog
        society={s}
        open={planOpen}
        onOpenChange={setPlanOpen}
        onSaved={onMutated}
      />

      {/* Suspend dialog */}
      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend {s.name}?</DialogTitle>
            <DialogDescription>
              The tenant is flagged as suspended. Existing data is preserved; you can unsuspend later.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor={`suspend-reason-${s.id}`}>Reason</Label>
            <Input
              id={`suspend-reason-${s.id}`}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              maxLength={300}
              placeholder="e.g. non-payment, pilot ended, fraud investigation…"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setSuspendOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={suspendMutation.isPending}
              loadingText="Suspending…"
              disabled={suspendReason.trim().length === 0}
              onClick={() => suspendMutation.mutate(suspendReason.trim())}
            >
              Suspend tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel-tenant dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel {s.name}?</DialogTitle>
            <DialogDescription>
              Marks the tenant as cancelled. Data is retained, but the tenant becomes
              eligible for permanent deletion. This action can be reversed only by an
              explicit unsuspend / edit flow.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setCancelOpen(false)}>
              Keep tenant
            </Button>
            <Button
              variant="danger"
              loading={cancelMutation.isPending}
              loadingText="Cancelling…"
              onClick={() => cancelMutation.mutate()}
            >
              Cancel tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-vt-alert">Permanently delete {s.name}?</DialogTitle>
            <DialogDescription>
              This irreversibly deletes the tenant and all related rows (residents, units,
              guards, bills, audit history). There is no undo. Only test tenants should
              ever reach this step.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDeleteOpen(false)}>
              Keep tenant
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              loadingText="Deleting…"
              onClick={() => deleteMutation.mutate()}
            >
              <Trash2 className="h-4 w-4" /> Delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Change-plan dialog — kebab-item shortcut to the same plan PATCH that the
// tenant-detail SubscriptionCard exposes. Reuses the row's plan fields as
// defaults so a partial save preserves existing values.
// ─────────────────────────────────────────────────────────────────────────
interface PlanPatchBody {
  plan: SubscriptionPlan;
  plan_status: SubscriptionPlanStatus;
  plan_price_inr_per_month: number | null;
  plan_seats_units: number | null;
  plan_renews_at: string | null;
}

// Legacy fallback used if the catalog endpoint fails — keeps the dialog usable
// in offline / API-down conditions.
const LEGACY_PLAN_OPTIONS: ReadonlyArray<{
  code: string;
  label: string;
  price_inr_per_month: number;
  seats_cap_units: number | null;
}> = [
  { code: 'free', label: 'Free', price_inr_per_month: 0, seats_cap_units: 25 },
  { code: 'lite', label: 'Lite', price_inr_per_month: 499, seats_cap_units: 100 },
  { code: 'pro', label: 'Pro', price_inr_per_month: 1999, seats_cap_units: 500 },
  { code: 'enterprise', label: 'Enterprise', price_inr_per_month: 0, seats_cap_units: null }
];

function formatTierOption(t: {
  label: string;
  price_inr_per_month: number;
  seats_cap_units: number | null;
}): string {
  const priceLabel = t.price_inr_per_month > 0 ? ` — ₹${t.price_inr_per_month.toLocaleString('en-IN')}/mo` : '';
  const seatsLabel = t.seats_cap_units !== null ? ` (${t.seats_cap_units} units)` : ' (unlimited)';
  return `${t.label}${priceLabel}${seatsLabel}`;
}

function ChangePlanDialog({
  society,
  open,
  onOpenChange,
  onSaved
}: {
  society: SocietyRow;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onSaved: () => void;
}): JSX.Element {
  const [plan, setPlan] = useState<SubscriptionPlan>(society.plan);
  const [planStatus, setPlanStatus] = useState<SubscriptionPlanStatus>(society.plan_status);
  const [price, setPrice] = useState<string>(
    society.plan_price_inr_per_month === null ? '' : String(society.plan_price_inr_per_month)
  );
  const [seats, setSeats] = useState<string>(
    society.plan_seats_units === null ? '' : String(society.plan_seats_units)
  );
  const [renewsAt, setRenewsAt] = useState<string>(society.plan_renews_at?.slice(0, 10) ?? '');
  // Track whether user has manually overridden price/seats so we don't clobber
  // their entry when they later change the tier.
  const [priceOverridden, setPriceOverridden] = useState(false);
  const [seatsOverridden, setSeatsOverridden] = useState(false);

  // Pull catalog (shared cache w/ superadmin-plans + tenant-detail).
  const tiersQuery = useQuery({
    queryKey: ['superadmin', 'plan-tiers'],
    queryFn: () => get<PlanTierListResponse>('/superadmin/plan-tiers'),
    enabled: open
  });

  // Active, sorted tier list — falls back to the legacy 4 if the call fails.
  const tiers = useMemo(() => {
    const items = tiersQuery.data?.items;
    if (!items || items.length === 0) {
      if (tiersQuery.isError) return LEGACY_PLAN_OPTIONS;
      return [];
    }
    return items
      .filter((t) => t.is_active)
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code));
  }, [tiersQuery.data, tiersQuery.isError]);

  // Re-seed every time the dialog opens so stale edits don't carry over.
  useEffect(() => {
    if (!open) return;
    setPlan(society.plan);
    setPlanStatus(society.plan_status);
    setPrice(society.plan_price_inr_per_month === null ? '' : String(society.plan_price_inr_per_month));
    setSeats(society.plan_seats_units === null ? '' : String(society.plan_seats_units));
    setRenewsAt(society.plan_renews_at?.slice(0, 10) ?? '');
    setPriceOverridden(false);
    setSeatsOverridden(false);
  }, [open, society.id, society.plan, society.plan_status, society.plan_price_inr_per_month, society.plan_seats_units, society.plan_renews_at]);

  // When the user picks a different tier, auto-fill price + seats from the
  // catalog default — but never clobber a manual override.
  const onPlanChange = (next: string): void => {
    setPlan(next);
    const tier = tiers.find((t) => t.code === next);
    if (!tier) return;
    if (!priceOverridden) {
      setPrice(String(tier.price_inr_per_month));
    }
    if (!seatsOverridden) {
      setSeats(tier.seats_cap_units === null ? '' : String(tier.seats_cap_units));
    }
  };

  const mutation = useMutation({
    mutationFn: (body: PlanPatchBody) =>
      patch<SocietyRow>(`/superadmin/societies/${society.id}/plan`, body),
    onSuccess: () => {
      toast.success(`"${society.name}" subscription updated`);
      onOpenChange(false);
      onSaved();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const submit = (): void => {
    const priceTrimmed = price.trim();
    const seatsTrimmed = seats.trim();
    const renewsTrimmed = renewsAt.trim();
    mutation.mutate({
      plan,
      plan_status: planStatus,
      plan_price_inr_per_month: priceTrimmed === '' ? null : Number.parseInt(priceTrimmed, 10),
      plan_seats_units: seatsTrimmed === '' ? null : Number.parseInt(seatsTrimmed, 10),
      plan_renews_at: renewsTrimmed === '' ? null : new Date(renewsTrimmed).toISOString()
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change plan — {society.name}</DialogTitle>
          <DialogDescription>
            Updates the tenant's subscription plan, status, price, seats, and renewal date.
            Leave price or seats blank to use the plan default.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-vt-3 text-vt-13">
          <label className="flex flex-col gap-vt-1">
            <span className="text-vt-text-soft">Plan</span>
            <Select
              value={plan}
              onChange={(e) => onPlanChange(e.target.value)}
              disabled={tiersQuery.isLoading}
            >
              {/* Always include the current plan code as a fallback option so the
                  select can render a value even if it's not in the active list
                  (e.g. tier was deactivated after assignment). */}
              {tiers.length === 0 && !tiersQuery.isLoading ? (
                <option value={society.plan} disabled>
                  Add tiers in /superadmin/plans
                </option>
              ) : null}
              {tiers.find((t) => t.code === plan) ? null : (
                <option value={plan}>{plan} (current)</option>
              )}
              {tiers.map((t) => (
                <option key={t.code} value={t.code}>
                  {formatTierOption(t)}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex flex-col gap-vt-1">
            <span className="text-vt-text-soft">Status</span>
            <Select
              value={planStatus}
              onChange={(e) => setPlanStatus(e.target.value as SubscriptionPlanStatus)}
            >
              <option value="active">Active</option>
              <option value="past_due">Past due</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </label>
          <div className="grid grid-cols-2 gap-vt-3">
            <label className="flex flex-col gap-vt-1">
              <span className="text-vt-text-soft">Price (₹/month)</span>
              <Input
                type="number"
                min="0"
                value={price}
                placeholder="Plan default"
                onChange={(e) => {
                  setPrice(e.target.value);
                  setPriceOverridden(true);
                }}
              />
            </label>
            <label className="flex flex-col gap-vt-1">
              <span className="text-vt-text-soft">Unit seats</span>
              <Input
                type="number"
                min="1"
                value={seats}
                placeholder="Plan default"
                onChange={(e) => {
                  setSeats(e.target.value);
                  setSeatsOverridden(true);
                }}
              />
            </label>
          </div>
          <label className="flex flex-col gap-vt-1">
            <span className="text-vt-text-soft">Renews on</span>
            <Input type="date" value={renewsAt} onChange={(e) => setRenewsAt(e.target.value)} />
          </label>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={mutation.isPending} loadingText="Saving…">
            Save plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Section A — Recent platform events
// Pulls the 8 most recent rows from GET /v1/superadmin/audit-log, renders
// the top 5 with a yellow NBH activity row + actor-role pill + relative
// timestamp. Right-action links into the full audit page.
// ──────────────────────────────────────────────────────────────────────

interface AuditRow {
  id: string;
  society_id: string | null;
  society_name: string | null;
  actor_user_id: string | null;
  actor_role: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  user_agent: string | null;
  at: string;
}

interface AuditListResponse {
  items: AuditRow[];
  next_cursor: string | null;
}

/**
 * Humanise an action verb like "society.suspend" → "Society Suspend".
 * Splits on dots and underscores, Title-Cases each token.
 */
function humaniseAction(action: string): string {
  return action
    .split(/[._]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

function RecentPlatformEventsCard(): JSX.Element {
  const q = useQuery<AuditListResponse>({
    queryKey: ['superadmin', 'audit', 'dashboard'],
    queryFn: () => get<AuditListResponse>('/superadmin/audit-log?limit=8'),
    retry: false
  });

  const rows = (q.data?.items ?? []).slice(0, 5);

  return (
    <div>
      <NbhSectionHeader
        title="Recent platform events"
        subtitle="Cross-tenant audit feed"
        palette="yellow"
        rightAction={
          <Button asChild size="sm" variant="ghost">
            <Link to="/superadmin/audit">View all</Link>
          </Button>
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="p-vt-3">
          {q.isLoading ? (
            <div className="flex flex-col gap-vt-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : q.isError ? (
            <div className="px-vt-2 py-vt-3 text-vt-13 text-vt-text-muted">
              {getErrorMessage(q.error)}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ClipboardCheck className="h-7 w-7" />}
              title="No recent platform events"
              description="As tenants and operators act, audit events appear here."
            />
          ) : (
            <div className="flex flex-col gap-vt-1">
              {rows.map((row) => (
                <NbhActivityRow
                  key={row.id}
                  icon={ClipboardCheck}
                  palette="yellow"
                  text={humaniseAction(row.action)}
                  meta={row.society_name ?? (row.entity_type ?? 'global')}
                  trailing={
                    <div className="flex items-center gap-vt-2">
                      {row.actor_role ? (
                        <Badge variant="muted" className="capitalize">
                          {row.actor_role.replace(/_/g, ' ')}
                        </Badge>
                      ) : null}
                      <span className="whitespace-nowrap text-vt-12 text-vt-text-soft">
                        {formatRelative(row.at)}
                      </span>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Section B — Trial / expiring tenants
// Derived from the same tenant list the table uses. Filters to trials
// whose trial_expires_at sits in the next 7 days, sorts ascending so
// the most-urgent appear first. Palette escalates from peach → salmon
// when the row has <= 2 days left (or is already expired).
// ──────────────────────────────────────────────────────────────────────

function TrialExpiringTenantsCard({ items }: { items: SocietyRow[] }): JSX.Element {
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

  const expiring = items
    .filter(
      (t) =>
        t.tenant_status === 'trial' &&
        t.trial_expires_at !== null &&
        new Date(t.trial_expires_at).getTime() - now <= sevenDaysMs
    )
    .sort((a, b) => {
      const ax = a.trial_expires_at ? new Date(a.trial_expires_at).getTime() : Infinity;
      const bx = b.trial_expires_at ? new Date(b.trial_expires_at).getTime() : Infinity;
      return ax - bx;
    })
    .slice(0, 5);

  return (
    <div>
      <NbhSectionHeader
        title="Trial · expiring soon"
        subtitle="Trials ending within 7 days"
        palette="salmon"
      />
      <Card className="rounded-vt-lg">
        <CardContent className="p-vt-3">
          {expiring.length === 0 ? (
            <EmptyState
              icon={<Check className="h-7 w-7" />}
              title="All trials healthy"
              description="No trials expire in the next 7 days."
            />
          ) : (
            <div className="flex flex-col gap-vt-1">
              {expiring.map((t) => {
                const expiresAt = t.trial_expires_at ? new Date(t.trial_expires_at).getTime() : null;
                const diffMs = expiresAt !== null ? expiresAt - now : 0;
                const daysLeft = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
                const expired = diffMs <= 0;
                const escalate = expired || daysLeft <= 2;
                const palette = escalate ? 'salmon' : 'peach';
                const p = nbhPalette(palette);
                const label = expired
                  ? 'expired'
                  : daysLeft === 1
                    ? '1 day left'
                    : `${daysLeft} days left`;
                return (
                  <div
                    key={t.id}
                    className="flex items-center gap-vt-3 rounded-vt-sm px-vt-2 py-vt-2 transition-colors duration-vt hover:bg-vt-surface-2"
                  >
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full shadow-vt-1 ring-2 ring-white/60"
                      style={{ backgroundColor: p.fg }}
                    >
                      <Clock className="h-4 w-4 text-white" strokeWidth={2.5} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-14 font-semibold tracking-[-0.01em] text-vt-text">
                        {t.name}
                      </div>
                      <div className="mt-0.5 flex items-center gap-vt-2">
                        <Badge variant={PLAN_BADGE(t.plan)} className="capitalize">
                          {t.plan}
                        </Badge>
                        <span className="truncate text-vt-12 text-vt-text-muted">
                          {t.jurisdiction_tag}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-vt-1">
                      <span
                        className="rounded-vt-pill px-2 py-0.5 text-[11px] font-semibold leading-none"
                        style={{ backgroundColor: p.bgFrom, color: p.fgDeep }}
                      >
                        {label}
                      </span>
                      <Link
                        to="/superadmin/tenants/$id"
                        params={{ id: t.id }}
                        className="text-vt-12 font-medium text-vt-primary hover:underline"
                      >
                        Open
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Section C — Top tenants by units
// Sorts the in-memory tenant list by _count.units desc and renders the
// top 5 with a numbered crown disc, name, jurisdiction tag, and a units
// + residents trailing chip.
// ──────────────────────────────────────────────────────────────────────

function TopTenantsByUnitsCard({ items }: { items: SocietyRow[] }): JSX.Element {
  const ranked = [...items]
    .sort((a, b) => (b._count?.units ?? 0) - (a._count?.units ?? 0))
    .slice(0, 5);

  const p = nbhPalette('lavender');

  return (
    <div>
      <NbhSectionHeader
        title="Top tenants by units"
        subtitle="Largest societies on the platform"
        palette="lavender"
      />
      <Card className="rounded-vt-lg">
        <CardContent className="p-vt-3">
          {ranked.length === 0 ? (
            <EmptyState
              icon={<Crown className="h-7 w-7" />}
              title="No tenants yet"
              description="Provision the first society to see it ranked here."
            />
          ) : (
            <div className="flex flex-col gap-vt-1">
              {ranked.map((t, idx) => (
                <Link
                  key={t.id}
                  to="/superadmin/tenants/$id"
                  params={{ id: t.id }}
                  className="group flex items-center gap-vt-3 rounded-vt-sm px-vt-2 py-vt-2 transition-colors duration-vt hover:bg-vt-surface-2"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-vt-13 font-bold text-white shadow-vt-1 ring-2 ring-white/60"
                    style={{ backgroundColor: p.fg }}
                  >
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-vt-14 font-semibold tracking-[-0.01em] text-vt-text group-hover:text-vt-primary">
                      {t.name}
                    </div>
                    <div className="mt-0.5 truncate text-vt-12 text-vt-text-muted">
                      {t.jurisdiction_tag}
                      {t.city ? ` · ${t.city}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end leading-tight">
                    <span className="font-mono text-vt-14 font-semibold" style={{ color: p.fgDeep }}>
                      {(t._count?.units ?? 0).toLocaleString('en-IN')} units
                    </span>
                    <span className="text-vt-12 text-vt-text-soft">
                      {(t._count?.residents ?? 0).toLocaleString('en-IN')} residents
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Section D — Revenue trend (last 30 days)
// Tiny sparkline-style bar chart driven by /v1/superadmin/revenue-trends.
// One mint-gradient bar per day. Headline shows today's MRR + delta vs
// 30d ago. Footer surfaces paid_tenants from the most-recent bucket.
// ──────────────────────────────────────────────────────────────────────

interface RevenueTrendBucket {
  bucket_start: string;
  mrr_inr: number;
  arr_inr: number;
  paid_tenants: number;
}

interface RevenueTrendResponse {
  from: string;
  to: string;
  bucket: 'day' | 'week';
  series: RevenueTrendBucket[];
  totals: {
    current_mrr_inr: number;
    starting_mrr_inr: number;
    growth_pct: number;
    peak_mrr_inr: number;
    peak_on: string | null;
  };
}

function formatInrCompact(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 10_000_000) return `${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000) return `${(n / 100_000).toFixed(1)}L`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function RevenueTrendCard(): JSX.Element {
  // Pin the window to "last 30 days" (day bucket) so the chart matches
  // the section title regardless of the server's default window.
  const { fromIso, toIso } = useMemo(() => {
    const now = new Date();
    const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
    const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
    return { fromIso: from.toISOString(), toIso: to.toISOString() };
  }, []);

  const q = useQuery<RevenueTrendResponse>({
    queryKey: ['superadmin', 'revenue-trends', 30],
    queryFn: () =>
      get<RevenueTrendResponse>(
        `/superadmin/revenue-trends?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}&bucket=day`
      ),
    retry: false
  });

  const palette = nbhPalette('mint');
  const series = q.data?.series ?? [];
  const maxMrr = Math.max(1, ...series.map((b) => b.mrr_inr));
  const totals = q.data?.totals;
  const currentMrr = totals?.current_mrr_inr ?? 0;
  const startingMrr = totals?.starting_mrr_inr ?? 0;
  const deltaInr = currentMrr - startingMrr;
  const growthPct = totals?.growth_pct ?? 0;
  const trendUp = deltaInr >= 0;

  // Footer counts come from the last bucket; trial count derived from the
  // outer tenant list isn't available here, but paid_tenants is canonical.
  const lastBucket = series[series.length - 1];
  const paidTenants = lastBucket?.paid_tenants ?? 0;

  // Anchor x-axis dates — day 1, 10, 20, and last (30) of the window.
  const anchorIdx = series.length > 0
    ? [0, Math.floor(series.length * 0.33), Math.floor(series.length * 0.66), series.length - 1]
    : [];

  return (
    <div>
      <NbhSectionHeader
        title="Revenue trend"
        subtitle="MRR over the last 30 days"
        palette="mint"
      />
      <Card className="rounded-vt-lg">
        <CardContent className="p-vt-4">
          {q.isLoading ? (
            <div className="flex flex-col gap-vt-3">
              <Skeleton className="h-8 w-40" />
              <div className="flex h-[140px] items-end gap-1">
                {Array.from({ length: 30 }).map((_, i) => (
                  <Skeleton key={i} className="h-full flex-1" />
                ))}
              </div>
            </div>
          ) : q.isError ? (
            <div className="px-vt-2 py-vt-3 text-vt-13 text-vt-text-muted">
              {getErrorMessage(q.error)}
            </div>
          ) : series.length === 0 ? (
            <EmptyState
              icon={<TrendingUp className="h-7 w-7" />}
              title="No revenue data yet"
              description="Once tenants are on a paid plan, daily MRR will chart here."
            />
          ) : (
            <div className="flex flex-col gap-vt-3">
              <div className="flex items-baseline justify-between gap-vt-3">
                <div className="min-w-0">
                  <div
                    className="text-vt-12 font-semibold uppercase tracking-[0.08em]"
                    style={{ color: palette.fgDeep }}
                  >
                    Current MRR
                  </div>
                  <div className="mt-vt-1 flex items-baseline gap-vt-2">
                    <span
                      className="font-mono text-vt-24 font-bold tracking-[-0.01em]"
                      style={{ color: palette.fgDeep }}
                    >
                      ₹{currentMrr.toLocaleString('en-IN')}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 rounded-vt-pill px-2 py-0.5 text-[11px] font-semibold leading-none"
                      style={{
                        backgroundColor: trendUp ? '#dcfce7' : '#fee2e2',
                        color: trendUp ? '#15803d' : '#b91c1c'
                      }}
                    >
                      {trendUp ? '+' : ''}
                      {growthPct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-vt-1 text-vt-12 text-vt-text-soft">
                    {trendUp ? '+' : ''}
                    ₹{deltaInr.toLocaleString('en-IN')} vs 30d ago
                  </div>
                </div>
                <span
                  aria-hidden="true"
                  className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full shadow-vt-1 ring-4 ring-white/60 sm:flex"
                  style={{ backgroundColor: palette.fg }}
                >
                  <TrendingUp className="h-6 w-6 text-white" strokeWidth={2.5} />
                </span>
              </div>

              {/* Bar sparkline — fixed-height row, flex children grow to fill. */}
              <div
                className="flex h-[140px] items-end gap-[2px] rounded-vt-sm bg-vt-surface-2 p-vt-2"
                role="img"
                aria-label={`Revenue trend over the last ${series.length} days`}
              >
                {series.map((bucket) => {
                  const ratio = bucket.mrr_inr / maxMrr;
                  const pct = Math.max(2, Math.round(ratio * 100));
                  return (
                    <div
                      key={bucket.bucket_start}
                      className="relative flex-1"
                      title={`${bucket.bucket_start} · ₹${bucket.mrr_inr.toLocaleString('en-IN')}`}
                    >
                      <div
                        className="w-full rounded-t-sm"
                        style={{
                          height: `${pct}%`,
                          background: `linear-gradient(180deg, ${palette.bgFrom} 0%, ${palette.fg} 100%)`
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* X-axis anchor dates */}
              {anchorIdx.length > 0 ? (
                <div className="flex items-center justify-between text-vt-12 text-vt-text-soft">
                  {anchorIdx.map((i) => (
                    <span key={i}>{series[i]?.bucket_start ?? ''}</span>
                  ))}
                </div>
              ) : null}

              <div className="text-vt-12 text-vt-text-muted">
                {paidTenants} paid · ₹{formatInrCompact(currentMrr)} today
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
